import React, { createContext, type ReactNode, useContext, useMemo } from 'react';

import { useIntl } from 'react-intl';

const FEEDBACK_CONTEXT_CF = 'customfield_10047';

export interface AIFeedbackProperties {
	displayName?: string;
	entrypointId: string;
	feedbackContext: string;
	feedbackContextId: string;
	locale: string;
	userId?: string;
}

type AIFeedbackPropertiesContextType = AIFeedbackProperties | undefined;

export const AIFeedbackPropertiesContext =
	createContext<AIFeedbackPropertiesContextType>(undefined);

type AIFeedbackPropertiesProviderProps = {
	children: ReactNode;
	displayName?: string;
	entrypointId: string;
	feedbackContext?: object;
	feedbackContextId?: string;
	locale?: string;
	userId?: string;
};

export const AIFeedbackPropertiesProvider = ({
	children,
	entrypointId,
	locale,
	feedbackContextId = FEEDBACK_CONTEXT_CF,
	feedbackContext = {},
	userId,
	displayName,
}: AIFeedbackPropertiesProviderProps): React.JSX.Element => {
	const { locale: intlLocale } = useIntl();

	const feedbackContextString = Object.entries(feedbackContext)
		.map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
		.join(' ');

	const contextValue = useMemo<AIFeedbackProperties>(
		() => ({
			entrypointId,
			feedbackContextId,
			feedbackContext: feedbackContextString,
			locale: locale || intlLocale,
			userId,
			displayName,
		}),
		[
			entrypointId,
			feedbackContextId,
			feedbackContextString,
			locale,
			intlLocale,
			userId,
			displayName,
		],
	);

	return (
		<AIFeedbackPropertiesContext.Provider value={contextValue}>
			{children}
		</AIFeedbackPropertiesContext.Provider>
	);
};

export const useAIFeedbackProperties = (): AIFeedbackProperties => {
	const context = useContext(AIFeedbackPropertiesContext);

	if (context === undefined) {
		throw new Error('useAIFeedbackProperties must be used within a AIFeedbackPropertiesProvider');
	}

	return context;
};
